import React, { startTransition, useMemo, useState } from 'react';
import { Outlet, useNavigate, useLocation, useParams, Navigate } from 'react-router-dom';
import {
  AppBar,
  Avatar,
  Box,
  Button,
  Chip,
  CssBaseline,
  Drawer,
  FormControl,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  Toolbar,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DashboardIcon from '@mui/icons-material/Dashboard';
import DescriptionIcon from '@mui/icons-material/Description';
import ReceiptIcon from '@mui/icons-material/Receipt';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import RefreshIcon from '@mui/icons-material/Refresh';
import { getAuthenticatedUploadUrl } from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';
import TopBarActions from '../../components/layout/TopBarActions';
import IdentityCarousel from '../../components/IdentityCarousel';
import ErrorBoundary from '../../components/ErrorBoundary';

const drawerWidth = 240;

/**
 * Shell visual no padrão Contratos/Jurídico, isolado:
 * Dashboard, Contratos, Notas Fiscais, Processo Administrativo.
 */
export default function ModuloFiscalLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { fiscalId } = useParams();
  const {
    user,
    fotoPerfil,
    unidadeSelecionada,
    unidades,
    setUnidadeSelecionada,
    isGlobalUser,
    logout,
    rbacRole,
    isFiscal,
    fiscalSetor,
  } = useAuth();

  const [drawerCollapsed, setDrawerCollapsed] = useState(false);
  const fotoPerfilUrl = fotoPerfil ? getAuthenticatedUploadUrl(fotoPerfil) : '';
  const globalUser = isGlobalUser();

  const menuItems = useMemo(
    () => [
      {
        text: 'Dashboard',
        icon: <DashboardIcon />,
        path: `/modulos/fiscal/${fiscalId}/dashboard`,
      },
      {
        text: 'Contratos',
        icon: <DescriptionIcon />,
        path: `/modulos/fiscal/${fiscalId}/contratos`,
      },
      {
        text: 'Notas Fiscais',
        icon: <ReceiptIcon />,
        path: `/modulos/fiscal/${fiscalId}/notas-fiscais`,
      },
      {
        text: 'Processo Administrativo',
        icon: <AccountTreeIcon />,
        path: `/modulos/fiscal/${fiscalId}/processo-administrativo`,
      },
    ],
    [fiscalId]
  );

  const formatCargoLabel = (s) =>
    s && typeof s === 'string' ? s.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()) : s;

  const cargoChipColor = (() => {
    if (rbacRole === 'LOCAL_APPROVER' || rbacRole === 'GLOBAL_VIEWER' || rbacRole === 'SUPER_ADMIN') {
      return 'warning';
    }
    if (
      rbacRole === 'LOCAL_ADMIN' ||
      rbacRole === 'LOCAL_OPERATOR' ||
      rbacRole === 'FINANCEIRO' ||
      rbacRole === 'FISCAL_MONITOR' ||
      rbacRole === 'FINANCEIRO_ANALISTA'
    ) {
      return 'primary';
    }
    return 'default';
  })();

  const cargoChipLabel = formatCargoLabel(
    user?.cargo ||
      (isFiscal ? fiscalSetor : null) ||
      (rbacRole ? rbacRole.replace(/_/g, ' ') : null) ||
      user?.tipo ||
      'Usuário'
  );

  const pageTitle = location.pathname.includes('/processo-administrativo')
    ? 'Processo Administrativo'
    : location.pathname.includes('/notas-fiscais')
      ? 'Notas Fiscais do Fiscal'
      : location.pathname.includes('/contratos/')
        ? 'Detalhe do Contrato'
        : location.pathname.includes('/contratos')
          ? 'Contratos do Fiscal'
          : 'Dashboard do Fiscal';

  const contratosListActive =
    location.pathname.endsWith('/contratos') ||
    /\/contratos\/[^/]+$/.test(location.pathname);
  const notasListActive = location.pathname.includes('/notas-fiscais');
  const paListActive = location.pathname.includes('/processo-administrativo');

  if (!fiscalId) {
    return <Navigate to="/home" replace />;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        width: '100%',
        maxWidth: '100vw',
        overflow: 'hidden',
        boxSizing: 'border-box',
        background: 'linear-gradient(135deg, #f8fafc 0%, #e3f2fd 50%, #f8fafc 100%)',
        position: 'relative',
        '&::before': {
          content: '"ISG"',
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          fontSize: '200px',
          fontWeight: 'bold',
          color: 'rgba(255, 255, 255, 0.03)',
          pointerEvents: 'none',
          userSelect: 'none',
          zIndex: 0,
        },
      }}
    >
      <CssBaseline />

      {drawerCollapsed && (
        <IconButton
          onClick={() => setDrawerCollapsed(false)}
          sx={{
            position: 'fixed',
            left: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 1301,
            bgcolor: 'background.paper',
            boxShadow: 2,
            borderRadius: '0 8px 8px 0',
            '&:hover': { bgcolor: 'primary.main', color: 'white' },
          }}
        >
          <ChevronRightIcon />
        </IconButton>
      )}

      <Drawer
        variant="permanent"
        sx={{
          width: drawerCollapsed ? 0 : drawerWidth,
          flexShrink: 0,
          transition: 'width 0.3s ease',
          [`& .MuiDrawer-paper`]: {
            width: drawerWidth,
            boxSizing: 'border-box',
            background: 'linear-gradient(180deg, rgba(255,255,255,0.95) 0%, rgba(240,248,255,0.9) 100%)',
            backdropFilter: 'blur(10px)',
            borderRight: '1px solid rgba(255,255,255,0.2)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
            borderRadius: '0 20px 20px 0',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            overflow: 'hidden',
            transition: 'margin 0.3s ease, width 0.3s ease',
            marginLeft: drawerCollapsed ? -drawerWidth : 0,
          },
        }}
      >
        <IconButton
          onClick={() => setDrawerCollapsed(true)}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 10,
            bgcolor: 'rgba(255,255,255,0.9)',
            '&:hover': { bgcolor: 'rgba(255,255,255,1)' },
          }}
        >
          <ChevronLeftIcon />
        </IconButton>

        <Box
          sx={{
            minHeight: { xs: 70, sm: 80, md: 100 },
            height: 'auto',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 40%, #7b1fa2 100%)',
            borderRadius: '0 20px 0 0',
            mb: 0,
            position: 'relative',
            overflow: 'hidden',
            boxShadow: '0 8px 32px 0 rgba(123,31,162,0.10), 0 1.5px 8px 0 rgba(25,118,210,0.10)',
          }}
        >
          <IconButton color="inherit" onClick={() => navigate('/perfil')} sx={{ p: 0, zIndex: 2 }}>
            <Avatar
              src={fotoPerfilUrl}
              onError={(e) => {
                e.target.style.display = 'none';
              }}
              sx={{
                width: { xs: 48, sm: 56, md: 64 },
                height: { xs: 48, sm: 56, md: 64 },
                bgcolor: 'primary.main',
                fontSize: { xs: 20, sm: 28, md: 32 },
                border: '4px solid #fff',
                objectFit: 'cover',
                objectPosition: 'center top',
              }}
            >
              {!fotoPerfil && (user?.nome || user)?.charAt(0)?.toUpperCase()}
            </Avatar>
          </IconButton>
        </Box>

        <Box sx={{ px: 2, py: 1 }}>
          {globalUser ? (
            <FormControl
              fullWidth
              size="small"
              sx={{ background: 'white', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
            >
              <Select
                value={unidadeSelecionada?.codigo || ''}
                onChange={(e) => {
                  const unidade = (unidades || []).find((u) => u.codigo === e.target.value);
                  if (unidade) setUnidadeSelecionada(unidade);
                }}
                displayEmpty
                sx={{
                  fontWeight: 600,
                  color: 'primary.main',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main',
                    borderWidth: 2,
                  },
                }}
              >
                {(unidades || []).map((unidade) => (
                  <MenuItem key={unidade.id || unidade.codigo} value={unidade.codigo}>
                    <Typography variant="body2" fontWeight={600}>
                      {unidade.nome}
                    </Typography>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <Typography
              variant="body2"
              fontWeight={700}
              color="primary.main"
              sx={{
                textAlign: 'center',
                px: 1,
                py: 1,
                borderRadius: 2,
                bgcolor: 'rgba(25,118,210,0.08)',
                border: '1px solid rgba(25,118,210,0.2)',
              }}
            >
              {unidadeSelecionada?.nome || 'Unidade'}
            </Typography>
          )}
        </Box>

        <List sx={{ px: 2, mt: 0, pt: 0, mb: 0, flex: 1, overflow: 'auto' }}>
          {menuItems.map((item) => {
            const active =
              item.text === 'Contratos'
                ? contratosListActive
                : item.text === 'Notas Fiscais'
                  ? notasListActive
                  : item.text === 'Processo Administrativo'
                    ? paListActive
                    : location.pathname === item.path;
            return (
              <ListItem
                button
                key={item.text}
                onClick={() => startTransition(() => navigate(item.path))}
                selected={active}
                sx={{
                  borderRadius: 3,
                  mb: 1,
                  transition: 'all 0.3s ease',
                  backgroundColor: active ? 'rgba(25,118,210,0.1)' : 'transparent',
                  '&:hover': {
                    background:
                      'linear-gradient(135deg, rgba(25,118,210,0.1) 0%, rgba(66,165,245,0.1) 100%)',
                    transform: 'translateX(5px)',
                    boxShadow: '0 4px 12px rgba(25,118,210,0.2)',
                  },
                }}
              >
                <ListItemIcon sx={{ color: 'primary.main' }}>{item.icon}</ListItemIcon>
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{
                    fontWeight: active ? 700 : 500,
                    fontSize: '0.9rem',
                  }}
                />
              </ListItem>
            );
          })}
        </List>

        <Box
          sx={{
            width: '100%',
            py: 3,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            borderTop: '1px solid #e3e3e3',
          }}
        >
          <Avatar
            src={fotoPerfilUrl}
            onError={(e) => {
              e.target.style.display = 'none';
            }}
            sx={{ width: 40, height: 40, mb: 1, bgcolor: 'primary.main', fontSize: 20 }}
          >
            {!fotoPerfil && (user?.nome || user)?.charAt(0)?.toUpperCase()}
          </Avatar>
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{ mb: 0.5, textAlign: 'center', maxWidth: 180, wordBreak: 'break-all' }}
          >
            {user?.nome || user}
          </Typography>
          <Chip
            label={cargoChipLabel}
            size="small"
            color={cargoChipColor}
            sx={{ mb: 1, fontWeight: 600, maxWidth: 180 }}
          />
          <Button
            variant="outlined"
            color="error"
            size="small"
            onClick={() => {
              logout();
              startTransition(() => navigate('/login'));
            }}
          >
            Sair
          </Button>
        </Box>
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: drawerCollapsed ? '100%' : `calc(100% - ${drawerWidth}px)`,
          minWidth: 0,
          maxWidth: '100%',
          height: '100vh',
          overflowX: 'hidden',
          overflowY: 'auto',
          p: { xs: 1.5, sm: 2, md: 3 },
          position: 'relative',
          zIndex: 1,
          boxSizing: 'border-box',
        }}
      >
        <AppBar
          position="static"
          elevation={0}
          sx={{
            background:
              'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(240,248,255,0.9) 100%)',
            backdropFilter: 'blur(10px)',
            color: '#1976d2',
            mb: 3,
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            borderBottom: '1px solid rgba(255,255,255,0.3)',
            borderRadius: '0 0 20px 20px',
          }}
        >
          <Toolbar sx={{ flexWrap: 'wrap', minHeight: 0, px: { xs: 1, sm: 2 } }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/home', { state: { openFiscalMap: true } })}
              size="small"
              sx={{
                mr: 2,
                color: 'primary.main',
                fontWeight: 600,
                flexShrink: 0,
                textTransform: 'none',
                '&:hover': { background: 'rgba(25, 118, 210, 0.1)' },
              }}
            >
              Voltar ao mapa
            </Button>
            <Typography
              variant="h5"
              fontWeight={700}
              sx={{
                flexGrow: 1,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {pageTitle}
            </Typography>
            <TopBarActions />
            <IconButton
              onClick={() => window.dispatchEvent(new CustomEvent('refreshPage'))}
              sx={{
                background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                color: 'white',
                boxShadow: '0 4px 12px rgba(25,118,210,0.3)',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 20px rgba(25,118,210,0.4)',
                },
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Toolbar>
        </AppBar>

        <IdentityCarousel />

        <ErrorBoundary>
          <Box sx={{ minWidth: 0, maxWidth: '100%', overflowX: 'hidden' }}>
            <Outlet />
          </Box>
        </ErrorBoundary>
      </Box>
    </Box>
  );
}
